import { Fragment, FunctionComponent } from "react";
import { useTranslation } from "react-i18next";

import { classNames } from "@/utils/classNames";

import { UserActivityRowConfig, UserActivityRowGroup } from "../config/userActivityRows";
import { UserActivityCaps } from "../schema/userActivity";
import { formatNumber } from "../utils/percentage";

export interface UserActivityGridProps {
	days: string[];
	todayKey?: string;
	groups: UserActivityRowGroup[];
	caps: UserActivityCaps;
	/** metric → day → count */
	values: Record<string, Record<string, number>>;
	/** Optional company-wide footer series (credit spend). */
	footer?: { labelKey: string; byDay: Record<string, number> };
}

const LABEL_COL =
	"sticky left-0 z-10 bg-brand-white px-3 py-1.5 text-left text-xs whitespace-nowrap border-r border-border-light";
const DAY_CELL = "px-3 py-1.5 text-right text-xs tabular-nums whitespace-nowrap";

/** `2026-08-04` → `Aug 4`, formatted from the parts so no timezone can shift the day. */
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

export const formatDayKey = (dayKey: string): string => {
	const [, month, day] = dayKey.split("-");
	const label = MONTHS[Number(month) - 1];

	return label ? `${label} ${Number(day)}` : dayKey;
};

/** Weekday initial, derived from the key at UTC noon so no local offset can shift it. */
export const weekdayOf = (dayKey: string): string =>
	new Date(`${dayKey}T12:00:00Z`).toLocaleDateString("en-US", {
		weekday: "short",
		timeZone: "UTC",
	});

/**
 * One user's day grid: metrics down the side, days across. The metric column is frozen so
 * a row stays identifiable while the days scroll.
 */
export const UserActivityGrid: FunctionComponent<UserActivityGridProps> = ({
	days,
	todayKey,
	groups,
	caps,
	values,
	footer,
}) => {
	const { t } = useTranslation();

	const renderCell = (row: UserActivityRowConfig, day: string) => {
		if (row.capField) {
			// The cap is the same every day; repeating it is what makes the row below readable.
			return <span className="text-text-meta">{formatNumber(caps[row.capField])}</span>;
		}

		const value = values[row.metric as string]?.[day] ?? 0;
		const cap = row.shareOfCap ? caps[row.shareOfCap] : 0;
		const share = row.shareOfCap && cap > 0 ? Math.round((value / cap) * 100) : null;

		return (
			<>
				<span className={value > 0 ? "text-text" : "text-text-disabled"}>
					{formatNumber(value)}
				</span>
				{share === null ? null : <span className="text-text-meta ml-1">{share}%</span>}
			</>
		);
	};

	return (
		<div className="w-full overflow-x-auto">
			<table className="border-separate border-spacing-0 text-sm">
				<thead>
					<tr>
						<th
							className={classNames(
								LABEL_COL,
								"font-semibold uppercase tracking-wide text-text-meta border-b border-border-light"
							)}
						>
							{t("analytics.users.metricColumn")}
						</th>
						{days.map((day) => (
							<th
								key={day}
								className={classNames(
									"px-3 py-1.5 text-right border-b border-border-light",
									day === todayKey ? "bg-blue-action-200" : ""
								)}
							>
								<span className="flex flex-col items-end leading-tight">
									<span
										className={classNames(
											"text-xs font-semibold",
											day === todayKey ? "text-brand-light" : "text-text-meta"
										)}
									>
										{day === todayKey ? t("analytics.users.today") : formatDayKey(day)}
									</span>
									<span className="text-[10px] text-text-disabled">{weekdayOf(day)}</span>
								</span>
							</th>
						))}
					</tr>
				</thead>
				<tbody>
					{groups.map((group) => (
						<Fragment key={group.key}>
							<tr className="bg-background-light">
								<td
									className={classNames(LABEL_COL, "bg-background-light font-semibold text-text")}
								>
									{t(group.labelKey)}
								</td>
								<td className="border-b border-border-light" colSpan={days.length} />
							</tr>
							{group.rows.map((row) => (
								<tr key={`${group.key}-${row.labelKey}`} className="hover:bg-background-light">
									<td
										className={classNames(
											LABEL_COL,
											row.capField ? "text-text-meta" : "text-text",
											row.isEngagement ? "pl-6" : ""
										)}
									>
										{t(row.labelKey)}
									</td>
									{days.map((day) => (
										<td
											key={day}
											className={classNames(
												DAY_CELL,
												"border-b border-border-light",
												day === todayKey ? "bg-blue-action-200/40" : ""
											)}
										>
											{renderCell(row, day)}
										</td>
									))}
								</tr>
							))}
						</Fragment>
					))}

					{footer ? (
						<tr className="bg-background-light/60">
							<td
								className={classNames(LABEL_COL, "bg-background-light/60 font-semibold text-text")}
							>
								{t(footer.labelKey)}
							</td>
							{days.map((day) => (
								<td key={day} className={classNames(DAY_CELL, "font-semibold text-text")}>
									{formatNumber(footer.byDay[day] ?? 0)}
								</td>
							))}
						</tr>
					) : null}
				</tbody>
			</table>
		</div>
	);
};
